import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Search, Coins, Image, Share2, Trash2, ChevronDown, ChevronRight } from 'lucide-react';

import { useWalletAddress } from '@/hooks/useWalletAddress';
import { TopNav } from '@/components/TopNav';
import { BottomNavigation } from '@/components/BottomNavigation';
import { RewardModal } from '@/components/RewardModal';
import { HistoryDrawer } from '@/components/HistoryDrawer';
import { FaqDrawer } from '@/components/FaqDrawer';
import { PrizeTiersDrawer } from '@/components/PrizeTiersDrawer';
import { SettingsSheet } from '@/components/SettingsSheet';
import { InvitationRequiredModal } from '@/components/InvitationRequiredModal';
import { InvitationCodeModal } from '@/components/InvitationCodeModal';
import { DustSwapFilterToggle } from '@/components/DustSwapFilterToggle';
import { ReclaimRentPanel } from '@/components/ReclaimRentPanel';
import { TokenValueBadge } from '@/components/TokenValueBadge';
import { DustSwapStatusIndicator } from '@/components/DustSwapStatusIndicator';
import { DustSwapSection } from '@/components/DustSwapSection';
import { SwapRoutePopup } from '@/components/SwapRoutePopup';
import { Tooltip } from '@/components/Tooltip';
import {
  getBurnableAssets,
  SWAP_BLACKLISTED_TOKENS,
  type BurnableAsset,
  type BurnableAssetsResult,
} from '@/utils/solana/dasApi';
import { getConnection, getSwapConnection, getSwapRpcUrl } from '@/utils/solana/connection';
import { calculateTotalReclaim } from '@/utils/solana/rentEstimator';
import { useBurnAssets } from '@/hooks/useBurnAssets';
import { useInvitationAccess } from '@/hooks/useInvitationAccess';
import { useAuth } from '@/hooks/useAuth';
import { useShare } from '@/hooks/useShare';
import { useResponsive } from '@/hooks/useResponsive';
import { useTokenPrices } from '@/hooks/useTokenPrices';
import { useDustSwap, DevnetNotSupportedError, RetryableTokensError, type DustSwapResult } from '@/hooks/useDustSwap';
import { useCombinedJunk } from '@/hooks/useCombinedJunk';
import { useGlobalConfig } from '@/hooks/useGlobalConfig';
import { CombinedDumpModal, type CombinedDumpStep, type OperationType } from '@/components/CombinedDumpModal';
import { isDustSwapEnabled, isMainnet, type SwappableToken } from '@/lib/solana/jupiter';
import { DUST_SWAP_CONSTANTS } from '@/lib/constants';
import config from '@/config';
import type { Reward } from '@/types/schemas';

interface TokenInfo {
  mint: string;
  symbol: string;
  name: string;
  decimals: number;
  logoUrl?: string;
  isValueless: boolean;
  currentValueLamports: bigint;
  balance?: number; // Raw balance
  uiAmount?: number; // Human-readable balance
  tokenAccount?: string; // SPL token account address for dust swapping
  isBlacklisted?: boolean; // Token is in blacklist - can only be swapped, not burned
  isSwapOnly?: boolean; // Token can only be swapped (e.g., FungibleAsset, Token-2022 with fees)
  lamports?: number; // Live rent deposit of the token account
}

interface NFTInfo {
  mint: string;
  name: string;
  symbol: string;
  image?: string;
  collection?: string;
  collectionName?: string;
}

interface SelectedItem {
  type: 'token' | 'nft';
  item: TokenInfo | NFTInfo;
}

// Feature flag: Control NFT junking via environment config
// Production: false (NFT tab hidden), Development/Staging: true (NFT tab visible)
// Configure via src/config/{production|staging|development}.ts
const ENABLE_NFT_JUNKING = config.ENABLE_NFT_JUNKING;
const ENABLE_RECLAIM_EXCESS_RENT = config.ENABLE_RECLAIM_EXCESS_RENT;
const SHOW_ASSET_DISCOVERY_DEBUG = Boolean(config.SHOW_ASSET_DISCOVERY_DEBUG);

export default function Home() {
  const navigate = useNavigate();
  const { address, isConnected, walletProvider } = useWalletAddress();
  const { mutate: burnAssets, isPending: isBurning } = useBurnAssets();
  const { hasInviter } = useInvitationAccess(); // checkAccess disabled - see handleDump()
  const { isAuthenticated, authenticate } = useAuth();
  const { shareJunk, getPotentialPoints, isSharing } = useShare();
  const { isMobile } = useResponsive();
  const { mutateAsync: executeDustSwap, isPending: isSwapping } = useDustSwap();
  const { mutateAsync: executeCombinedJunk, isPending: isCombinedJunking } = useCombinedJunk();
  const { globalConfig } = useGlobalConfig();

  const [rewardModalData, setRewardModalData] = useState<{
    show: boolean;
    rewards: Reward[];
  }>({ show: false, rewards: [] });

  const [historyOpen, setHistoryOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [prizeTiersOpen, setPrizeTiersOpen] = useState(false);
  const [showInviteRequired, setShowInviteRequired] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);

  const [activeTab, setActiveTab] = useState<'tokens' | 'nfts'>('tokens');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [tokens, setTokens] = useState<TokenInfo[]>([]);
  const [nfts, setNfts] = useState<NFTInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [burnableAssets, setBurnableAssets] = useState<BurnableAsset[]>([]);
  const [nftDiscoveryError, setNftDiscoveryError] = useState<string | null>(
    null
  );
  const [assetDiscoveryDebug, setAssetDiscoveryDebug] = useState<string | null>(
    null
  );

  // Burn success modal state
  const [showBurnSuccess, setShowBurnSuccess] = useState(false);
  const [lastBurnResult, setLastBurnResult] = useState<{
    signatures: string[];
    chestsEarned: number;
    totalLamportsEarned: bigint;
    assetsCount: number;
    rewards: Array<{ assetIndex: number; amountSol: number; amount: bigint }>;
    grossSol: string;
    feeSol: string;
    netSol: string;
    pointsEarned: number;
    tokenCount: number;
    nftCount: number;
    dustSwapSolReceived: number;
    tokensSwapped: number;
  }>({
    signatures: [],
    chestsEarned: 0,
    totalLamportsEarned: BigInt(0),
    assetsCount: 0,
    rewards: [],
    grossSol: '0',
    feeSol: '0',
    netSol: '0',
    pointsEarned: 0,
    tokenCount: 0,
    nftCount: 0,
    dustSwapSolReceived: 0,
    tokensSwapped: 0,
  });

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Dust swap state
  const [dustSwapFilterActive, setDustSwapFilterActive] = useState(false);
  const [dustSwapExpanded, setDustSwapExpanded] = useState(false);
  const [showSwapRoutePopup, setShowSwapRoutePopup] = useState<string | null>(null);

  // Combined dump modal state (handles swap, burn, or both)
  const [showCombinedModal, setShowCombinedModal] = useState(false);
  const [combinedOperationType, setCombinedOperationType] = useState<OperationType>('burn_only');
  const [combinedStep, setCombinedStep] = useState<CombinedDumpStep>('preparing');
  const [swapTokenCount, setSwapTokenCount] = useState(0);
  const [swapCurrentIndex, setSwapCurrentIndex] = useState(0);
  const [swapCurrentSymbol, setSwapCurrentSymbol] = useState('');
  const [estimatedSwapSol, setEstimatedSwapSol] = useState(0);
  const [burnTokenCount, setBurnTokenCount] = useState(0);
  const [burnNftCount, setBurnNftCount] = useState(0);
  const [estimatedBurnSol, setEstimatedBurnSol] = useState(0);
  const [combinedAttemptCount, setCombinedAttemptCount] = useState(0);
  const [combinedError, setCombinedError] = useState<string | undefined>();

  // Get embedded wallet flag from wallet provider
  const isEmbeddedWallet = walletProvider?.isEmbeddedWallet ?? false;

  const applyAssetLoadResult = useCallback((result: BurnableAssetsResult) => {
    const tokenAssets: TokenInfo[] = [];
    const nftAssets: NFTInfo[] = [];

    for (const asset of result.assets) {
      if (asset.isNFT) {
        nftAssets.push({
          mint: asset.mint,
          name: asset.name,
          symbol: asset.symbol,
          image: asset.imageUrl || '/Chest.svg',
          collection: asset.collection,
          collectionName: asset.collectionName,
        });
      } else {
        tokenAssets.push({
          mint: asset.mint,
          symbol: asset.symbol,
          name: asset.name,
          decimals: asset.decimals || 0,
          logoUrl: asset.imageUrl,
          isValueless: true,
          currentValueLamports: BigInt(0),
          balance: asset.balance,
          uiAmount: asset.uiAmount,
          tokenAccount: asset.tokenAccount,
          isBlacklisted: asset.isBlacklisted,
          isSwapOnly: asset.isSwapOnly,
          lamports: asset.lamports,
        });
      }
    }

    setBurnableAssets(result.assets);
    setTokens(tokenAssets);
    setNfts(nftAssets);
    setNftDiscoveryError(
      result.diagnostics.nftDiscoveryUnavailable
        ? 'NFT discovery is temporarily unavailable. Token balances may still be shown, but NFTs could not be loaded from DAS.'
        : null
    );

    if (SHOW_ASSET_DISCOVERY_DEBUG) {
      const interfaceSummary = Object.entries(result.diagnostics.interfaceCounts)
        .map(([assetInterface, count]) => `${assetInterface}:${count}`)
        .join(', ');
      const debugParts = [`Asset source: ${result.diagnostics.source}`];
      if (result.diagnostics.dasFailed && result.diagnostics.dasError) {
        debugParts.push(`DAS error: ${result.diagnostics.dasError}`);
      }
      if (interfaceSummary) {
        debugParts.push(`DAS counts: ${interfaceSummary}`);
      }
      setAssetDiscoveryDebug(debugParts.join(' | '));
    } else {
      setAssetDiscoveryDebug(null);
    }
  }, []);

  // Token prices hook for dust swapping
  const {
    prices,
    isLoading: pricesLoading,
    fetchPrices,
    refreshPrices,
    isTokenSwappable,
    hasTokenSwapRoute,
    tokenMeetsMinimum,
    getTokenPrice,
  } = useTokenPrices();

  // Load user's tokens and NFTs
  useEffect(() => {
    const loadUserAssets = async () => {
      if (!isConnected || !address) {
        setTokens([]);
        setNfts([]);
        setBurnableAssets([]);
        setNftDiscoveryError(null);
        setAssetDiscoveryDebug(null);
        return;
      }

      setIsLoading(true);
      try {
        // For dust swap, use mainnet RPC to detect mainnet tokens (even in dev mode)
        // This allows testing swap routes with real mainnet tokens
        const useSwapRpc = isDustSwapEnabled() && isMainnet();
        const connection = useSwapRpc ? getSwapConnection() : getConnection();
        const rpcUrl = useSwapRpc
          ? getSwapRpcUrl()
          : (config.SOLANA_RPC_URL || connection.rpcEndpoint);

        console.log(`📡 Loading assets from ${useSwapRpc ? 'swap (mainnet)' : 'regular'} RPC: ${rpcUrl.slice(0, 50)}...`);

        const result = await getBurnableAssets(address, rpcUrl, connection);
        console.log('📦 Found', result.assets.length, 'burnable assets');
        applyAssetLoadResult(result);
      } catch (error) {
        console.error('Error loading assets:', error);
        setBurnableAssets([]);
        setTokens([]);
        setNfts([]);
        setNftDiscoveryError(null);
        setAssetDiscoveryDebug(null);
        toast.error('Failed to load assets');
      } finally {
        setIsLoading(false);
      }
    };

    loadUserAssets();
  }, [isConnected, address, applyAssetLoadResult]);

  // Re-read the wallet's assets (and their live rent balances) after an on-chain change
  const reloadAssets = useCallback(async () => {
    if (!isConnected || !address) return;
    try {
      const useSwapRpc = isDustSwapEnabled() && isMainnet();
      const connection = useSwapRpc ? getSwapConnection() : getConnection();
      const rpcUrl = useSwapRpc ? getSwapRpcUrl() : (config.SOLANA_RPC_URL || connection.rpcEndpoint);
      const result = await getBurnableAssets(address, rpcUrl, connection);
      applyAssetLoadResult(result);
    } catch (error) {
      console.error('Error reloading assets:', error);
    }
  }, [isConnected, address, applyAssetLoadResult]);

  // Clear selected items when wallet disconnects
  useEffect(() => {
    if (!isConnected) {
      setSelectedItems([]);
    }
  }, [isConnected]);

  // Derived state: tokens that are eligible for dust swapping (has route AND meets minimum)
  const swappableTokens = useMemo(() => {
    if (!isDustSwapEnabled()) return [];
    return tokens.filter(token =>
      isTokenSwappable(token.mint) && !SWAP_BLACKLISTED_TOKENS.has(token.mint)
    );
  }, [tokens, isTokenSwappable]);

  // Derived state: tokens that have swap routes (including those below minimum)
  const tokensWithRoutes = useMemo(() => {
    if (!isDustSwapEnabled()) return [];
    return tokens.filter(token => hasTokenSwapRoute(token.mint));
  }, [tokens, hasTokenSwapRoute]);

  // Derived state: selected tokens that are eligible for dust swapping
  const selectedSwappableTokens = useMemo(() => {
    return selectedItems
      .filter(item => item.type === 'token')
      .filter(item => swappableTokens.some(st => st.mint === item.item.mint))
      .map(item => item.item as TokenInfo);
  }, [selectedItems, swappableTokens]);

  // Derived state: displayed tokens (filtered by search and optionally by swappable filter)
  const displayedTokens = useMemo(() => {
    const searchFiltered = tokens.filter(
      token =>
        token.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
        token.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Filter tokens based on their status
    // - Blacklisted tokens: can ONLY be swapped (never burned), so hide if no swap route
    // - isSwapOnly tokens: can be burned if below swap threshold, so always show
    // - Regular tokens: always show
    const swapOnlyFiltered = searchFiltered.filter(token => {
      if (token.isBlacklisted) {
        // Blacklisted tokens can only be swapped - hide if no swap route available
        return isDustSwapEnabled() && tokensWithRoutes.some(st => st.mint === token.mint);
      }
      // isSwapOnly tokens and regular tokens: always show
      // (isSwapOnly tokens can be burned if below swap threshold)
      return true;
    });

    if (!dustSwapFilterActive || !isDustSwapEnabled()) {
      return swapOnlyFiltered;
    }

    // When dust swap filter is active, show only tokens that meet the minimum threshold
    // (hide greyed out tokens that are below minimum)
    return swapOnlyFiltered.filter(token =>
      swappableTokens.some(st => st.mint === token.mint)
    );
  }, [tokens, searchQuery, dustSwapFilterActive, tokensWithRoutes, swappableTokens]);

  // Fetch prices when dust swap is enabled and we have tokens
  useEffect(() => {
    if (isDustSwapEnabled() && tokens.length > 0 && address) {
      const swappableTokenInput: SwappableToken[] = tokens
        .filter(t => t.tokenAccount && t.balance && t.balance > 0)
        .map(t => ({
          mint: t.mint,
          tokenAccount: t.tokenAccount!,
          balance: t.balance!,
          decimals: t.decimals,
          symbol: t.symbol,
        }));

      if (swappableTokenInput.length > 0) {
        fetchPrices(swappableTokenInput);
      }
    }
  }, [tokens, address, fetchPrices]);

  const toggleItemSelection = (
    type: 'token' | 'nft',
    item: TokenInfo | NFTInfo
  ) => {
    setSelectedItems(prev => {
      const existingIndex = prev.findIndex(
        selected => selected.type === type && selected.item.mint === item.mint
      );

      if (existingIndex >= 0) {
        return prev.filter((_, index) => index !== existingIndex);
      } else {
        return [...prev, { type, item }];
      }
    });
  };

  const isItemSelected = (type: 'token' | 'nft', item: TokenInfo | NFTInfo) => {
    return selectedItems.some(
      selected => selected.type === type && selected.item.mint === item.mint
    );
  };

  const handleSelectAll = () => {
    if (activeTab === 'tokens') {
      // Use displayedTokens which respects both search and dust swap filter
      const newSelections: SelectedItem[] = displayedTokens.map(item => ({
        type: 'token' as const,
        item: item,
      }));
      setSelectedItems(newSelections);
    } else {
      const newSelections: SelectedItem[] = filteredNfts.map(item => ({
        type: 'nft' as const,
        item: item,
      }));
      setSelectedItems(newSelections);
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems([]);
  };

  const calculateRentEstimate = () => {
    if (selectedItems.length === 0) {
      console.log('🔍 [calculateRentEstimate] No items selected');
      return { gross: '0', fee: '0', net: '0' };
    }

    console.log(
      '🔍 [calculateRentEstimate] Selected items:',
      selectedItems.length
    );
    console.log(
      '🔍 [calculateRentEstimate] Burnable assets available:',
      burnableAssets.length
    );

    // Map selected items to their actual BurnableAsset types from burnableAssets
    // This ensures we use the correct asset type (spl_token, token_2022, metadata_nft, mpl_core_nft)
    const assets: BurnableAsset[] = selectedItems
      .map(selected => {
        const asset = burnableAssets.find(a => a.mint === selected.item.mint);
        console.log(
          '🔍 [calculateRentEstimate] Mapping mint:',
          selected.item.mint,
          '-> found:',
          !!asset
        );
        return asset;
      })
      .filter((asset): asset is BurnableAsset => asset !== undefined);

    console.log('🔍 [calculateRentEstimate] Mapped assets:', assets.length);

    if (assets.length === 0) {
      console.log(
        '🔍 [calculateRentEstimate] No valid assets found after mapping'
      );
      return { gross: '0', fee: '0', net: '0' };
    }

    const estimate = calculateTotalReclaim(assets, globalConfig?.tokenFeesBps, globalConfig?.nftFeesBps);

    console.log('🔍 [calculateRentEstimate] Estimate:', {
      gross: estimate.totalGrossLamports,
      fee: estimate.platformFeeLamports,
      net: estimate.totalNetLamports,
      feePercentage: estimate.feePercentage,
    });

    return {
      gross: (Number(estimate.totalGrossLamports) / 1e9).toFixed(6),
      fee: (Number(estimate.platformFeeLamports) / 1e9).toFixed(6),
      net: (Number(estimate.totalNetLamports) / 1e9).toFixed(6),
    };
  };

  const estimate = calculateRentEstimate();

  const calculatePointsEstimate = () => {
    // Separate swappable tokens from non-swappable (burn) tokens
    const swappableMints = new Set(selectedSwappableTokens.map(t => t.mint));

    const selectedTokens = selectedItems.filter(item => item.type === 'token');
    const selectedNfts = selectedItems.filter(item => item.type === 'nft');

    // Calculate swap points based on SOL value: 500,000 * SOL_value
    // This means for every 0.001 SOL worth of tokens, user gets 500 points
    const DUST_SWAP_POINTS_PER_SOL = 500000;
    const DUST_SWAP_INVITEE_BONUS = 80; // Per swap action

    let swapPoints = 0;
    let swapCount = 0;
    for (const item of selectedTokens) {
      if (swappableMints.has(item.item.mint)) {
        // Calculate points based on estimated SOL value
        const priceData = getTokenPrice(item.item.mint);
        if (priceData?.estimatedSolValue) {
          swapPoints += Math.floor(priceData.estimatedSolValue * DUST_SWAP_POINTS_PER_SOL);
          swapCount++;
        }
      }
    }

    // Calculate burn points for non-swappable tokens (excluding blacklisted which can only swap)
    const burnTokenCount = selectedTokens.filter(t =>
      !swappableMints.has(t.item.mint) && !t.item.isBlacklisted
    ).length;
    const nftCount = selectedNfts.length;
    const burnPoints = burnTokenCount * 100 + nftCount * 200;

    // Calculate invitee bonuses
    const burnInviteeBonus = hasInviter ? burnTokenCount * 40 + nftCount * 80 : 0;
    const swapInviteeBonus = hasInviter && swapCount > 0 ? DUST_SWAP_INVITEE_BONUS * swapCount : 0;

    return swapPoints + burnPoints + burnInviteeBonus + swapInviteeBonus;
  };

  const pointsEstimate = calculatePointsEstimate();

  // Note: filteredTokens is now replaced by displayedTokens (defined above)
  // which respects both search query and dust swap filter

  const filteredNfts = nfts.filter(
    nft =>
      nft.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (nft.collection &&
        nft.collection.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Group NFTs by collection for better UX
  const groupedNfts = useMemo(() => {
    const groups: Map<string, { name: string; address: string; image?: string; nfts: NFTInfo[] }> = new Map();

    for (const nft of filteredNfts) {
      const collectionKey = nft.collection || 'uncategorized';
      const collectionName = nft.collectionName || (nft.collection
        ? `${nft.collection.slice(0, 4)}...${nft.collection.slice(-4)}`
        : 'Uncategorized');

      if (!groups.has(collectionKey)) {
        groups.set(collectionKey, {
          name: collectionName,
          address: nft.collection || '',
          image: nft.image, // Use first NFT's image as collection thumbnail
          nfts: [],
        });
      }
      groups.get(collectionKey)!.nfts.push(nft);
    }

    // Sort groups by collection name, with "Uncategorized" at the end
    return Array.from(groups.entries()).sort((a, b) => {
      if (a[0] === 'uncategorized') return 1;
      if (b[0] === 'uncategorized') return -1;
      return a[1].name.localeCompare(b[1].name);
    });
  }, [filteredNfts]);

  // Track which collections user has manually collapsed
  const [collapsedCollections, setCollapsedCollections] = useState<Set<string>>(new Set());

  // Collections are expanded by default - we track collapsed ones instead
  const isCollectionExpanded = (key: string) => !collapsedCollections.has(key);

  // Toggle collection group expansion
  const toggleCollectionExpanded = (collectionKey: string) => {
    setCollapsedCollections(prev => {
      const next = new Set(prev);
      if (next.has(collectionKey)) {
        next.delete(collectionKey);
      } else {
        next.add(collectionKey);
      }
      return next;
    });
  };

  // Expand all collections (clear collapsed set)
  const expandAllCollections = () => {
    setCollapsedCollections(new Set());
  };

  // Collapse all collections
  const collapseAllCollections = () => {
    setCollapsedCollections(new Set(groupedNfts.map(([key]) => key)));
  };

  const handleDump = async () => {
    // INVITATION REQUIREMENT DISABLED
    // Uncomment the block below to re-enable invitation-only mode:
    // if (!checkAccess()) {
    //   setShowInviteRequired(true);
    //   return;
    // }

    // Check authentication - prompt user to sign in if not authenticated
    if (!isAuthenticated) {
      console.log(
        '🔐 [Home] User not authenticated, prompting authentication...'
      );
      toast.info('Please sign the message to verify your wallet ownership');
      const authenticated = await authenticate();
      if (!authenticated) {
        toast.error('Authentication required to burn assets');
        return;
      }
    }

    if (selectedItems.length === 0 || !walletProvider) {
      toast.error('Please select assets to burn');
      return;
    }

    // Map selected items to BurnableAsset format
    const assetsToburn: BurnableAsset[] = selectedItems
      .map(selected => {
        const asset = burnableAssets.find(a => a.mint === selected.item.mint);
        return asset;
      })
      .filter((asset): asset is BurnableAsset => asset !== undefined);

    if (assetsToburn.length === 0) {
      toast.error('No valid assets to burn');
      return;
    }

    // Validate all assets have valid mint addresses
    const validAssets = assetsToburn.filter(
      asset => asset && asset.mint && asset.mint.trim() !== ''
    );

    if (validAssets.length === 0) {
      toast.error(
        'No valid assets selected - all assets have invalid mint addresses'
      );
      console.error('❌ [Home] All assets have invalid mints:', assetsToburn);
      return;
    }

    if (validAssets.length < assetsToburn.length) {
      const invalidCount = assetsToburn.length - validAssets.length;
      toast.warning(
        `Skipped ${invalidCount} asset${invalidCount > 1 ? 's' : ''} with invalid mint addresses`
      );
      console.warn(
        '⚠️ [Home] Filtered out assets with invalid mints:',
        assetsToburn.filter(a => !a || !a.mint || a.mint.trim() === '')
      );
    }

    // ============================================================
    // DUST SWAP + BURN INTEGRATION (Combined operation with unified modal)
    // ============================================================

    // Identify which selected tokens are swappable (have swap routes)
    const swappableMints = new Set(selectedSwappableTokens.map(t => t.mint));

    // Build SwappableToken[] for dust swap execution
    const tokensForSwap: SwappableToken[] = selectedSwappableTokens
      .filter(t => t.tokenAccount && t.balance && t.balance > 0)
      .map(t => ({
        mint: t.mint,
        tokenAccount: t.tokenAccount!,
        balance: t.balance!,
        decimals: t.decimals,
        symbol: t.symbol,
        lamports: t.lamports,
      }));

    // Assets to burn = tokens not being swapped
    // - Exclude tokens that will be swapped (in swappableMints = above threshold)
    // - Exclude blacklisted tokens (they can only be swapped, never burned)
    // - Allow isSwapOnly tokens to burn IF they're not meeting swap threshold
    //   (isSwapOnly just means the token has extensions that made it swap-preferred,
    //    but if it can't be swapped, burning is still valid)
    const assetsToBurn = validAssets.filter(a => {
      // Never burn tokens that are being swapped (above threshold)
      if (swappableMints.has(a.mint)) return false;
      // Never burn blacklisted tokens - they must be swapped
      if (a.isBlacklisted) return false;
      // isSwapOnly tokens CAN be burned if they're not meeting swap threshold
      // (they're not in swappableMints, so they're below threshold or have no route)
      return true;
    });

    // Determine operation type
    const hasSwap = isDustSwapEnabled() && isMainnet() && tokensForSwap.length > 0;
    const hasBurn = assetsToBurn.length > 0;
    const operationType: OperationType = hasSwap && hasBurn
      ? 'swap_and_burn'
      : hasSwap
        ? 'swap_only'
        : 'burn_only';

    console.log('🔄 [handleDump] Combined operation:', {
      operationType,
      totalSelected: selectedItems.length,
      swappableTokens: tokensForSwap.length,
      assetsToBurn: assetsToBurn.length,
    });

    // Calculate burn estimate for the modal
    const burnEstimateForModal = hasBurn ? calculateTotalReclaim(assetsToBurn, globalConfig?.tokenFeesBps, globalConfig?.nftFeesBps) : null;
    const burnTokensCount = assetsToBurn.filter(a => !a.isNFT).length;
    const burnNftsCount = assetsToBurn.filter(a => a.isNFT).length;

    // Initialize combined modal state
    setShowCombinedModal(true);
    setCombinedOperationType(operationType);
    setCombinedStep('preparing');
    setSwapTokenCount(tokensForSwap.length);
    setSwapCurrentIndex(0);
    setSwapCurrentSymbol(tokensForSwap[0]?.symbol || '');
    setEstimatedSwapSol(0);
    setBurnTokenCount(burnTokensCount);
    setBurnNftCount(burnNftsCount);
    setEstimatedBurnSol(burnEstimateForModal ? Number(burnEstimateForModal.totalNetLamports) / 1e9 : 0);
    setCombinedAttemptCount(0);
    setCombinedError(undefined);

    let swapResult: DustSwapResult | null = null;
    let tokensSwappedSuccessfully: string[] = [];

    // ============================================================
    // COMBINED JUNK FLOW: Single signing for swap + burn
    // ============================================================
    if (operationType === 'swap_and_burn') {
      try {
        setCombinedStep('signing');

        // Convert BurnableAsset to BurnAsset format
        const burnAssetsForCombined = assetsToBurn.map(a => ({
          mint: a.mint,
          type: a.type,
          isNFT: a.isNFT,
          tokenAccount: a.tokenAccount,
          metadata: a.metadata,
          masterEdition: a.masterEdition,
          collection: a.collection,
          collectionMetadata: a.collectionMetadata,
        }));

        const combinedResult = await executeCombinedJunk({
          tokensToSwap: tokensForSwap,
          assetsToBurn: burnAssetsForCombined,
          onProgress: (step, details) => {
            if (step === 'preparing') {
              setCombinedStep('preparing');
            } else if (step === 'signing') {
              setCombinedStep('signing');
            } else if (step === 'swapping') {
              setCombinedStep('swapping');
            } else if (step === 'burning') {
              setCombinedStep('burning');
            } else if (step === 'complete') {
              setCombinedStep('complete');
            }

            if (details.currentIndex !== undefined) {
              if (details.phase === 'swap') {
                setSwapCurrentIndex(details.currentIndex);
              }
            }
            if (details.totalCount !== undefined) {
              if (details.phase === 'swap') {
                setSwapTokenCount(details.totalCount);
              }
            }
            if (details.currentSymbol) {
              setSwapCurrentSymbol(details.currentSymbol);
            }
            if (details.estimatedSolOutput !== undefined) {
              setEstimatedSwapSol(details.estimatedSolOutput);
            }
          },
        });

        // Show success modal
        setCombinedStep('complete');
        await new Promise(resolve => setTimeout(resolve, 1000));
        setShowCombinedModal(false);

        // Calculate rewards
        const swapSolReceived = combinedResult.estimatedSolFromSwap + combinedResult.estimatedSolFromSwapRent;
        const SWAP_POINTS_PER_SOL = 500000;
        const SWAP_INVITEE_BONUS = 80;
        const swapBasePoints = Math.floor(combinedResult.estimatedSolFromSwap * SWAP_POINTS_PER_SOL);
        const swapBonusPoints = hasInviter ? SWAP_INVITEE_BONUS * combinedResult.swappedTokens : 0;
        const totalSwapPoints = swapBasePoints + swapBonusPoints;

        const burnEstimate = calculateTotalReclaim(assetsToBurn, globalConfig?.tokenFeesBps, globalConfig?.nftFeesBps);
        const burnNetSol = Number(burnEstimate.totalNetLamports) / 1e9;

        // Show success modal
        setLastBurnResult({
          signatures: combinedResult.signatures,
          chestsEarned: 0,
          totalLamportsEarned: BigInt(0),
          assetsCount: combinedResult.burnedAssets,
          rewards: [],
          grossSol: (swapSolReceived + burnNetSol).toFixed(6),
          feeSol: (Number(burnEstimate.platformFeeLamports) / 1e9).toFixed(6),
          netSol: (swapSolReceived + burnNetSol).toFixed(6),
          pointsEarned: totalSwapPoints,
          tokenCount: assetsToBurn.filter(a => !a.isNFT).length,
          nftCount: assetsToBurn.filter(a => a.isNFT).length,
          dustSwapSolReceived: swapSolReceived,
          tokensSwapped: combinedResult.swappedTokens,
        });
        setShowBurnSuccess(true);

        // Clear selection and update UI
        setSelectedItems([]);
        const allProcessedMints = new Set([
          ...tokensForSwap.map(t => t.mint),
          ...assetsToBurn.map(a => a.mint),
        ]);
        setBurnableAssets(prev => prev.filter(a => !allProcessedMints.has(a.mint)));
        setTokens(prev => prev.filter(t => !allProcessedMints.has(t.mint)));
        setNfts(prev => prev.filter(n => !allProcessedMints.has(n.mint)));

        return;
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Operation failed';
        if (errorMsg.includes('Rate limit')) {
          setCombinedError('Rate limit reached. Please wait a moment and try again.');
          toast.warning('Rate limit reached', {
            description: 'Jupiter is temporarily busy. Please wait a few seconds and try again.',
            duration: 5000,
          });
        } else {
          setCombinedError(errorMsg);
          toast.error('Operation failed', { description: errorMsg });
        }
        setShowCombinedModal(false);
        return;
      }
    }

    // ============================================================
    // SEPARATE FLOWS: Swap-only or Burn-only operations
    // ============================================================

    // STEP 1: Execute swap if needed (swap-only operation)
    if (hasSwap && !hasBurn) {
      try {
        setCombinedStep('signing');

        swapResult = await executeDustSwap({
          tokens: tokensForSwap,
          onProgress: (step, details) => {
            // Map dust swap steps to combined modal steps
            if (step === 'quoting' || step === 'building') {
              setCombinedStep('preparing');
            } else if (step === 'signing') {
              setCombinedStep('signing');
            } else if (step === 'sending' || step === 'confirming') {
              setCombinedStep('swapping');
            }

            if (details.currentTokenIndex !== undefined) {
              setSwapCurrentIndex(details.currentTokenIndex);
            }
            if (details.totalTokens !== undefined) {
              setSwapTokenCount(details.totalTokens);
            }
            if (details.currentTokenSymbol) {
              setSwapCurrentSymbol(details.currentTokenSymbol);
            }
            if (details.estimatedSolOutput !== undefined) {
              setEstimatedSwapSol(details.estimatedSolOutput);
            }
            if (details.attemptCount !== undefined) {
              setCombinedAttemptCount(details.attemptCount);
            }
          },
        });

        toast.success(
          `Swapped ${swapResult.swappedTokens} tokens for ${swapResult.estimatedSolReceived.toFixed(6)} SOL`,
          { id: 'dust-swap' }
        );

        // Track which tokens were successfully swapped
        tokensSwappedSuccessfully = tokensForSwap
          .filter(t => !swapResult!.nonSwappableTokens.includes(t.mint))
          .filter(t => !swapResult!.retryableTokens.includes(t.mint))
          .map(t => t.mint);

      } catch (error) {
        if (error instanceof DevnetNotSupportedError) {
          // On devnet, just proceed with burn (expected behavior)
          console.log('[handleDump] Devnet detected, skipping swap');
        } else if (error instanceof RetryableTokensError) {
          // Rate limit or temporary error - prompt user to retry
          console.log('[handleDump] Rate limit hit, prompting retry', {
            retryableTokens: error.retryableTokens,
            nonSwappableTokens: error.nonSwappableTokens,
          });
          setCombinedError('Rate limit reached. Please wait a moment and try again.');
          toast.warning('Rate limit reached', {
            description: 'Jupiter is temporarily busy. Please wait a few seconds and try again.',
            duration: 5000,
          });

          // Close modal to allow user to retry
          setShowCombinedModal(false);
          return;
        } else {
          // Swap failed - show error
          const errorMsg = error instanceof Error ? error.message : 'Failed to swap tokens';
          setCombinedError(`Swap failed: ${errorMsg}`);
          toast.error('Swap failed', { description: errorMsg });

          // If swap-only operation, close modal and return
          setShowCombinedModal(false);
          return;
        }
      }
    }

    console.log('🔥 [handleDump] After swap:', {
      swappedSuccessfully: tokensSwappedSuccessfully.length,
      assetsToBurn: assetsToBurn.length,
    });

    // ============================================================
    // HANDLE SWAP-ONLY SCENARIO (No burn needed)
    // ============================================================

    // Only proceed with burn if there are non-swappable assets to burn
    if (assetsToBurn.length === 0) {
      // All selected tokens were swappable - just update UI after swap
      if (tokensSwappedSuccessfully.length > 0) {
        // Show complete step before closing
        setCombinedStep('complete');
        await new Promise(resolve => setTimeout(resolve, 1000));
        setShowCombinedModal(false);

        // Calculate swap rewards for display
        const swapSolReceived = swapResult?.estimatedSolReceived || 0;
        // Points calculation: 500,000 * SOL_value (swap only, not rent) + invitee bonus (80 per token)
        const SWAP_POINTS_PER_SOL = 500000;
        const SWAP_INVITEE_BONUS = 80;
        // Use only swap SOL for points, not rent reclaim (to match backend calculation)
        const swapOnlySol = swapResult?.estimatedSolFromSwap || 0;
        const swapBasePoints = Math.floor(swapOnlySol * SWAP_POINTS_PER_SOL);
        const swapBonusPoints = hasInviter ? SWAP_INVITEE_BONUS * tokensSwappedSuccessfully.length : 0;
        const totalSwapPoints = swapBasePoints + swapBonusPoints;

        // Show swap success modal
        setLastBurnResult({
          signatures: swapResult?.signatures || [],
          chestsEarned: 0,
          totalLamportsEarned: BigInt(0),
          assetsCount: 0,
          rewards: [],
          grossSol: swapSolReceived.toFixed(6),
          feeSol: '0',
          netSol: swapSolReceived.toFixed(6),
          pointsEarned: totalSwapPoints,
          tokenCount: tokensSwappedSuccessfully.length,
          nftCount: 0,
          dustSwapSolReceived: swapSolReceived,
          tokensSwapped: tokensSwappedSuccessfully.length,
        });
        setShowBurnSuccess(true);

        // Clear selection, update UI
        setSelectedItems([]);
        // Remove swapped tokens from UI
        const swappedSet = new Set(tokensSwappedSuccessfully);
        setBurnableAssets(prev => prev.filter(a => !swappedSet.has(a.mint)));
        setTokens(prev => prev.filter(t => !swappedSet.has(t.mint)));
      } else {
        // No tokens swapped successfully
        setShowCombinedModal(false);
      }
      return;
    }

    // ============================================================
    // PROCEED WITH BURN (Using assetsToBurn instead of validAssets)
    // ============================================================

    console.log('✅ [Home] Burning', assetsToBurn.length, 'valid assets');

    // Calculate rent estimate before burning (to show accurate rewards)
    console.log(
      '🔍 [handleDump] Calculating estimate for',
      assetsToBurn.length,
      'assets'
    );
    console.log(
      '🔍 [handleDump] Asset types:',
      assetsToBurn.map(a => ({
        mint: a.mint.slice(0, 8),
        type: a.type,
        isNFT: a.isNFT,
      }))
    );

    const burnEstimate = calculateTotalReclaim(assetsToBurn, globalConfig?.tokenFeesBps, globalConfig?.nftFeesBps);

    console.log('🔍 [handleDump] Burn estimate:', {
      totalGrossLamports: burnEstimate.totalGrossLamports,
      platformFeeLamports: burnEstimate.platformFeeLamports,
      totalNetLamports: burnEstimate.totalNetLamports,
      feePercentage: burnEstimate.feePercentage,
    });

    const estimatedGross = (
      Number(burnEstimate.totalGrossLamports) / 1e9
    ).toFixed(6);
    const estimatedFee = (
      Number(burnEstimate.platformFeeLamports) / 1e9
    ).toFixed(6);
    const estimatedNet = (Number(burnEstimate.totalNetLamports) / 1e9).toFixed(
      6
    );

    console.log('🔍 [handleDump] Formatted estimates:', {
      grossSol: estimatedGross,
      feeSol: estimatedFee,
      netSol: estimatedNet,
    });

    // Helper function to extract simulation logs from error message
    const extractSimulationLogs = (errorMessage: string): string[] => {
      // Try to extract logs from error message like:
      // "Simulation failed. Message: ... Logs: ["log1", "log2", ...]"
      const logsMatch = errorMessage.match(/Logs:\s*\[([^\]]+)\]/);
      if (logsMatch) {
        try {
          // Parse the logs array - it's embedded as JSON-like string
          const logsStr = `[${logsMatch[1]}]`;
          // Clean up escaped quotes and parse
          const cleanLogs = logsStr.replace(/\\"/g, '"');
          return JSON.parse(cleanLogs);
        } catch {
          // Fallback: split by quotes
          const logMatches = logsMatch[1].match(/"([^"]+)"/g);
          if (logMatches) {
            return logMatches.map(l => l.replace(/"/g, ''));
          }
        }
      }
      return [];
    };

    // Store swap info for use in success handler
    const swapSolReceived = swapResult?.estimatedSolReceived || 0;
    // Use swap-only SOL for points (not rent reclaim) to match backend calculation
    const swapOnlySol = swapResult?.estimatedSolFromSwap || 0;
    const swappedCount = tokensSwappedSuccessfully.length;

    // Update modal to burning step
    setCombinedStep(hasSwap ? 'burning' : 'signing');
    setCombinedAttemptCount(0);

    // Call useBurnAssets hook mutation with backend co-signing
    burnAssets(
      { assets: assetsToBurn },
      {
        onError: (error: Error) => {
          console.error('🔥 [Home] Burn error:', error);
          const errorMessage = error.message || String(error);

          // Close the combined modal on error
          setShowCombinedModal(false);

          // Extract simulation logs if present
          const simulationLogs = extractSimulationLogs(errorMessage);

          // Parse error for user-friendly title and details
          let title = 'Burn Failed';
          let details = errorMessage;

          // Detect specific error types
          if (errorMessage.includes('not a Master Edition') || errorMessage.includes('0x6d')) {
            title = 'NFT Burn Failed';
            details = 'This NFT cannot be burned because it is not a Master Edition. It may be a Print Edition or have an incompatible metadata format.';
          } else if (errorMessage.includes('Account is frozen') || errorMessage.includes('0x11')) {
            title = 'Token Frozen';
            details = 'This token account is frozen and cannot be burned. The token issuer may have frozen this asset.';
          } else if (errorMessage.includes('Missing collection') || errorMessage.includes('Collection')) {
            title = 'Collection Error';
            details = 'This NFT has a collection verification issue. It may require additional accounts to burn.';
          } else if (errorMessage.includes('insufficient lamports') || errorMessage.includes('0x1')) {
            title = 'Insufficient SOL';
            details = 'You do not have enough SOL to complete this transaction. Please add more SOL to your wallet.';
          } else if (errorMessage.includes('User rejected') || errorMessage.includes('cancelled')) {
            // Don't show toast for user cancellations
            return;
          }

          // Build full error description with logs
          let fullDescription = details;
          if (simulationLogs.length > 0) {
            const relevantLogs = simulationLogs
              .filter(log => log.includes('failed') || log.includes('Error') || log.includes('error'))
              .slice(0, 3);
            if (relevantLogs.length > 0) {
              fullDescription += '\n\nLogs: ' + relevantLogs.join(' | ');
            }
          }

          toast.error(title, {
            description: fullDescription,
            duration: 15000, // Show longer for debugging
          });
        },
        onSuccess: async data => {
          console.log('🎉 Burn success:', data);

          // Show complete step
          setCombinedStep('complete');
          await new Promise(resolve => setTimeout(resolve, 1000));
          setShowCombinedModal(false);

          // Calculate points based on burned assets only
          // Base: 100 per token, 200 per NFT
          // Invitee bonus (if has inviter): +40 per token, +80 per NFT
          const tokenCount = assetsToBurn.filter(a => !a.isNFT).length;
          const nftCount = assetsToBurn.filter(a => a.isNFT).length;
          const burnBasePoints = tokenCount * 100 + nftCount * 200;
          const burnInviteeBonus = hasInviter ? tokenCount * 40 + nftCount * 80 : 0;
          const burnPoints = burnBasePoints + burnInviteeBonus;

          // Calculate dust swap points: 500,000 * SOL_value (swap only, not rent) + 80 per swap (if has inviter)
          const SWAP_POINTS_PER_SOL = 500000;
          const SWAP_INVITEE_BONUS = 80;
          // Use swap-only SOL for points, not rent reclaim (to match backend calculation)
          const swapBasePoints = Math.floor(swapOnlySol * SWAP_POINTS_PER_SOL);
          const swapInviteeBonus = hasInviter && swappedCount > 0 ? SWAP_INVITEE_BONUS * swappedCount : 0;
          const swapPoints = swapBasePoints + swapInviteeBonus;

          // Total points = burn points + swap points
          const totalPoints = burnPoints + swapPoints;

          // Total credits = burn credits (account rent) + swap SOL received
          const totalCredits = parseFloat(estimatedNet) + swapSolReceived;

          // Set burn success modal data with actual estimates and swap info
          setLastBurnResult({
            signatures: [data.signature],
            chestsEarned: 0,
            totalLamportsEarned: BigInt(0),
            assetsCount: data.assetsCount,
            rewards: [],
            grossSol: estimatedGross,
            feeSol: estimatedFee,
            netSol: totalCredits.toFixed(6),
            pointsEarned: totalPoints,
            tokenCount,
            nftCount,
            dustSwapSolReceived: swapSolReceived,
            tokensSwapped: swappedCount,
          });
          setShowBurnSuccess(true);

          // Clear selection after successful burn
          setSelectedItems([]);

          // Optimistic update: Immediately remove burned AND swapped assets from UI
          const processedMints = new Set([
            ...assetsToBurn.map(a => a.mint),
            ...tokensSwappedSuccessfully,
          ]);
          setBurnableAssets(prev => prev.filter(a => !processedMints.has(a.mint)));
          setTokens(prev => prev.filter(t => !processedMints.has(t.mint)));
          setNfts(prev => prev.filter(n => !processedMints.has(n.mint)));

          // Reload assets after burn (with delay to allow blockchain finalization)
          if (address) {
            const loadAssets = async () => {
              try {
                // Wait 2 seconds for transaction to finalize on-chain
                await new Promise(resolve => setTimeout(resolve, 2000));

                const connection = getConnection();
                const rpcUrl = config.SOLANA_RPC_URL || connection.rpcEndpoint;
                const result = await getBurnableAssets(
                  address,
                  rpcUrl,
                  connection
                );
                applyAssetLoadResult(result);
              } catch (error) {
                console.error('Error reloading assets:', error);
              }
            };
            loadAssets();
          }
        },
      }
    );
  };

  return (
    <div className="min-h-screen">
      <TopNav
        onHistoryClick={() => setHistoryOpen(true)}
        onFaqClick={() => setFaqOpen(true)}
        onPrizeTiersClick={() => setPrizeTiersOpen(true)}
        onSettingsClick={() => setSettingsOpen(true)}
        onNFTGalleryClick={() => navigate('/my-nft')}
      />

      <main className="flex-1 p-4 pb-24">
        {/* Mobile: Selected Items Preview at Top */}
        {isMobile && selectedItems.length > 0 && (
          <div className="mb-4">
            <div
              className="rounded-lg border-2 p-4"
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                borderStyle: 'dashed',
                borderColor: '#FFFFFF',
              }}
            >
              {/* Summary Stats */}
              <div className="mb-4 grid grid-cols-3 gap-3 text-sm">
                <div
                  className="rounded p-2 text-center"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                >
                  <div
                    className="text-gray-400"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    Assets
                  </div>
                  <div
                    className="font-bold text-yellow-400"
                    style={{
                      fontFamily: 'Outfit, sans-serif',
                      fontWeight: 'bold',
                    }}
                  >
                    {selectedItems.length}
                  </div>
                </div>
                <div
                  className="rounded p-2 text-center"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                >
                  <div
                    className="text-gray-400"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    Credits
                  </div>
                  <div
                    className="flex items-center justify-center gap-1 font-bold text-yellow-400"
                    style={{
                      fontFamily: 'Outfit, sans-serif',
                      fontWeight: 'bold',
                    }}
                  >
                    {estimate.net} SOL
                    <div className="text-2xl">💰</div>
                  </div>
                </div>
                <div
                  className="rounded p-2 text-center"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                >
                  <div
                    className="text-gray-400"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    Points
                  </div>
                  <div
                    className="flex items-center justify-center gap-1 font-bold text-green-400"
                    style={{
                      fontFamily: 'Outfit, sans-serif',
                      fontWeight: 'bold',
                    }}
                  >
                    {pointsEstimate}
                    <img
                      src="/points.svg"
                      alt="Points"
                      className="inline h-4 w-4"
                    />
                  </div>
                </div>
              </div>

              {/* Selected Items Grid */}
              <div
                className="mb-3 text-sm font-medium text-white"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Selected Items:
              </div>
              <div className="max-h-[200px] overflow-y-auto">
                <div className="grid grid-cols-3 gap-2">
                  {selectedItems.map((selected, index) => {
                    // Check if this token is swappable (mobile view)
                    const isSwappableMobile = selected.type === 'token' &&
                      selectedSwappableTokens.some(t => t.mint === selected.item.mint);

                    return (
                      <div
                        key={`${selected.type}-${selected.item.mint}-${index}`}
                        className="relative overflow-hidden rounded-lg border"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          borderColor: isSwappableMobile ? '#4ADE80' : '#50E5F8',
                        }}
                      >
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            toggleItemSelection(selected.type, selected.item);
                          }}
                          className="absolute top-0.5 right-0.5 z-10 flex h-5 w-5 items-center justify-center rounded-full text-xs text-white transition-colors hover:bg-red-500"
                          style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
                        >
                          ×
                        </button>

                        <div className="p-1.5">
                          <div
                            className="mb-1 flex aspect-square items-center justify-center rounded"
                            style={{ backgroundColor: '#FFFFFF' }}
                          >
                            {selected.type === 'token' &&
                            'logoUrl' in selected.item &&
                            selected.item.logoUrl ? (
                              <img
                                src={selected.item.logoUrl}
                                alt={selected.item.symbol}
                                className="h-8 w-8 rounded-full"
                                onError={e => {
                                  (e.target as HTMLImageElement).src =
                                    '/token-placeholder.svg';
                                }}
                              />
                            ) : selected.type === 'nft' &&
                              'image' in selected.item &&
                              selected.item.image ? (
                              <img
                                src={selected.item.image}
                                alt={selected.item.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <img
                                src="/token-placeholder.svg"
                                alt={selected.item.symbol}
                                className="h-8 w-8 object-contain"
                              />
                            )}
                          </div>

                          {/* Swappable Credits value (mobile) */}
                          {isSwappableMobile && (
                            <div
                              className="mb-0.5 text-center text-[9px] font-semibold"
                              style={{
                                fontFamily: 'Outfit, sans-serif',
                                color: '#4ADE80',
                              }}
                            >
                              {(() => {
                                const priceData = getTokenPrice(selected.item.mint);
                                const value = priceData?.estimatedSolValue || 0;
                                if (value === 0) return '0 Credits';
                                if (value < 0.0001) return '<0.0001 Credits';
                                if (value < 0.001) return `${value.toFixed(4)} Credits`;
                                if (value < 0.01) return `${value.toFixed(3)} Credits`;
                                return `${value.toFixed(2)} Credits`;
                              })()}
                            </div>
                          )}

                          <div className="px-1 text-center">
                            <div
                              className="line-clamp-2 text-xs font-bold text-white"
                              style={{
                                fontFamily: 'Outfit, sans-serif',
                                lineHeight: '1.2',
                                minHeight: '28px',
                              }}
                              title={selected.item.symbol}
                            >
                              {selected.item.symbol}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Side-by-side layout: Left (tokens/NFTs) and Right (selected items) */}
        <div className="flex flex-col gap-4 md:flex-row">
          {/* Left Panel: Tabs, Search, and Assets Grid - 60% width */}
          <div className="w-full md:w-3/5">
            {/* Tabs and Search */}
            <div className="mb-4 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
              {/* ... existing code ... */}
              <div className="flex w-full gap-2 sm:w-auto sm:gap-3">
                <div className="flex flex-1 sm:flex-initial">
                  <button
                    onClick={() => setActiveTab('tokens')}
                    className={`px-3 py-2 sm:px-4 sm:py-3 ${ENABLE_NFT_JUNKING ? 'rounded-tl-lg' : 'rounded-lg'} flex flex-1 items-center justify-center gap-1 text-xs font-medium transition-all sm:flex-initial sm:gap-2 sm:text-base ${
                      activeTab === 'tokens' ? 'text-white' : 'text-white/70'
                    }`}
                    style={{
                      fontFamily: 'Outfit, sans-serif',
                      backgroundColor:
                        activeTab === 'tokens' ? '#FFF' : '#343434',
                      color: activeTab === 'tokens' ? '#014A5C' : 'white',
                    }}
                  >
                    <Coins className="h-4 w-4 sm:h-5 sm:w-5" />
                    Tokens
                  </button>
                  {ENABLE_NFT_JUNKING && (
                    <button
                      onClick={() => setActiveTab('nfts')}
                      className={`flex flex-1 items-center justify-center gap-1 rounded-tr-lg px-3 py-2 text-xs font-medium transition-all sm:flex-initial sm:gap-2 sm:px-4 sm:py-3 sm:text-base ${
                        activeTab === 'nfts' ? 'text-white' : 'text-white/70'
                      }`}
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        backgroundColor:
                          activeTab === 'nfts' ? '#FFF' : '#343434',
                        color: activeTab === 'nfts' ? '#014A5C' : 'white',
                      }}
                    >
                      <Image className="h-4 w-4 sm:h-5 sm:w-5" />
                      NFTs
                    </button>
                  )}
                </div>

                {/* Select All / Deselect All Button */}
                <button
                  onClick={
                    selectedItems.length > 0
                      ? handleDeselectAll
                      : handleSelectAll
                  }
                  className="flex-1 rounded-lg px-3 py-2 text-xs font-medium transition-all hover:scale-105 sm:flex-initial sm:px-4 sm:py-3 sm:text-base"
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    backgroundColor:
                      selectedItems.length > 0 ? '#EF4444' : '#50E5F8',
                    color: selectedItems.length > 0 ? '#FFF' : '#000',
                  }}
                >
                  {selectedItems.length > 0 ? 'DESELECT ALL' : 'SELECT ALL'}
                </button>
              </div>

              <div className="relative w-full sm:w-auto">
                <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full rounded-lg border-2 py-2 pr-4 pl-10 text-sm sm:w-auto"
                  style={{
                    backgroundColor: '#FFF',
                    border: '2px solid #358094',
                    color: '#000',
                    fontFamily: 'Outfit, sans-serif',
                  }}
                />
              </div>
            </div>

            {/* Excess rent reclaim - withdraws surplus rent deposits without closing accounts */}
            {ENABLE_RECLAIM_EXCESS_RENT && isConnected && activeTab === 'tokens' && (
              <ReclaimRentPanel className="mb-4" onReclaimed={reloadAssets} />
            )}

            {/* Dust Swap Filter Toggle - Only show when feature is enabled and on tokens tab */}
            {isDustSwapEnabled() && activeTab === 'tokens' && (
              <div className="mb-4">
                <DustSwapFilterToggle
                  active={dustSwapFilterActive}
                  onToggle={setDustSwapFilterActive}
                  swappableCount={
                    // Count displayed tokens that are swappable (meet minimum threshold)
                    displayedTokens.filter(t => swappableTokens.some(st => st.mint === t.mint)).length
                  }
                  totalCount={displayedTokens.length}
                  disabled={isLoading}
                  isLoading={pricesLoading}
                />
              </div>
            )}

            {/* Assets Grid - with fixed height and internal scrolling */}
            <div
              className="mb-6 flex flex-col overflow-hidden rounded-lg p-4 sm:p-6"
              style={{ backgroundColor: '#FFFFFF', maxHeight: '600px' }}
            >
              {assetDiscoveryDebug && (
                <div
                  className="mb-3 rounded-lg border px-3 py-2 text-xs"
                  style={{
                    borderColor: nftDiscoveryError ? '#FB8B00' : '#358094',
                    backgroundColor: nftDiscoveryError
                      ? 'rgba(251, 139, 0, 0.08)'
                      : 'rgba(1, 74, 92, 0.04)',
                    color: '#014A5C',
                    fontFamily: 'Outfit, sans-serif',
                  }}
                >
                  {assetDiscoveryDebug}
                </div>
              )}
              {isLoading || isBurning ? (
                <div className="overflow-y-auto">
                  <div className="grid grid-cols-3 gap-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-6">
                    {Array.from({ length: 12 }).map((_, index) => (
                      <div
                        key={index}
                        className="aspect-square animate-pulse rounded-lg bg-gray-700"
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {activeTab === 'tokens' ? (
                    <div className="overflow-y-auto">
                      {!isConnected ? (
                        <div className="py-8 text-center">
                          <p
                            className="text-gray-700"
                            style={{ fontFamily: 'Outfit, sans-serif' }}
                          >
                            Connect your wallet to see your tokens
                          </p>
                        </div>
                      ) : displayedTokens.length === 0 ? (
                        <div className="py-8 text-center">
                          <p
                            className="text-gray-700"
                            style={{ fontFamily: 'Outfit, sans-serif' }}
                          >
                            {searchQuery
                              ? 'No tokens found'
                              : dustSwapFilterActive
                                ? 'No swappable tokens found'
                                : 'No tokens in your wallet'}
                          </p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 gap-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-6">
                          {displayedTokens.map(token => {
                            // Check if token is below minimum AND blacklisted (can't do anything with it)
                            // Non-blacklisted tokens below minimum can still be burned, so don't grey them out
                            const isBelowMinimum = isDustSwapEnabled() &&
                              token.isBlacklisted &&
                              hasTokenSwapRoute(token.mint) &&
                              !tokenMeetsMinimum(token.mint);
                            const tokenEstimatedValue = getTokenPrice(token.mint)?.estimatedSolValue;

                            // Tooltip content for blacklisted tokens below minimum (can't burn or swap)
                            const belowMinimumTooltip = isBelowMinimum ? (
                              <div className="space-y-1">
                                <div className="font-semibold text-yellow-400">Cannot Junk This Token</div>
                                <div>
                                  Current value: <span className="text-gray-300">{tokenEstimatedValue !== undefined ? `${tokenEstimatedValue.toFixed(6)} Credits` : 'Unknown'}</span>
                                </div>
                                <div>
                                  Minimum required: <span className="text-green-400">{DUST_SWAP_CONSTANTS.MIN_SWAP_VALUE_SOL} Credits</span>
                                </div>
                                <div className="pt-1 text-gray-400 text-[10px]">
                                  This token can only be swapped and is below the minimum threshold.
                                </div>
                              </div>
                            ) : null;

                            const tokenCard = (
                            <div
                              className={`relative rounded-lg border-2 p-2 transition-all ${
                                isItemSelected('token', token)
                                  ? 'scale-95 border-yellow-400 bg-yellow-400/10 cursor-pointer'
                                  : isBelowMinimum
                                    ? 'border-gray-700 opacity-60 cursor-not-allowed'
                                    : 'border-gray-600 hover:border-white hover:bg-white/5 cursor-pointer'
                              }`}
                              onClick={() => {
                                // Don't allow selection of tokens below minimum
                                if (!isBelowMinimum) {
                                  toggleItemSelection('token', token);
                                }
                              }}
                            >
                              {/* Marked for Junk Overlay */}
                              {isItemSelected('token', token) && (
                                <div className="bg-opacity-75 pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg bg-black">
                                  <Trash2
                                    className="mb-1 h-8 w-8"
                                    style={{ color: '#FB8B00' }}
                                  />
                                  <div
                                    className="px-2 text-center text-xs font-bold text-white"
                                    style={{ fontFamily: 'Outfit, sans-serif' }}
                                  >
                                    MARKED FOR JUNK
                                  </div>
                                </div>
                              )}

                              {/* Dust Swap Status Indicator */}
                              {isDustSwapEnabled() && !isItemSelected('token', token) && (
                                <DustSwapStatusIndicator
                                  isSwappable={isTokenSwappable(token.mint)}
                                  hasSwapRoute={hasTokenSwapRoute(token.mint)}
                                  meetsMinimum={tokenMeetsMinimum(token.mint)}
                                  estimatedSolValue={getTokenPrice(token.mint)?.estimatedSolValue}
                                  compact
                                />
                              )}

                              <div
                                className="flex aspect-square w-full items-center justify-center rounded"
                                style={{ backgroundColor: '#FFFFFF' }}
                              >
                                <img
                                  src={
                                    token.logoUrl || '/token-placeholder.svg'
                                  }
                                  alt={token.symbol}
                                  className="h-12 w-12 rounded-full object-contain"
                                  onError={e => {
                                    const img = e.target as HTMLImageElement;
                                    if (
                                      img.src !==
                                      window.location.origin +
                                        '/token-placeholder.svg'
                                    ) {
                                      img.src = '/token-placeholder.svg';
                                      img.className =
                                        'w-12 h-12 object-contain';
                                    }
                                  }}
                                />
                              </div>
                              <div className="mt-2 min-h-[72px] text-center">
                                <div
                                  className="text-sm font-bold text-gray-900"
                                  style={{ fontFamily: 'Outfit, sans-serif' }}
                                >
                                  {token.symbol}
                                </div>
                                <div
                                  className="truncate text-xs text-gray-600"
                                  style={{ fontFamily: 'Outfit, sans-serif' }}
                                  title={token.name}
                                >
                                  {token.name}
                                </div>
                                <div
                                  className="mt-1 text-xs font-semibold"
                                  style={{
                                    fontFamily: 'Outfit, sans-serif',
                                    color: '#50E5F8',
                                  }}
                                >
                                  {token.uiAmount !== undefined
                                    ? token.uiAmount === 0
                                      ? '0'
                                      : token.uiAmount < 0.0001
                                        ? '<0.0001'
                                        : token.uiAmount.toLocaleString(
                                            undefined,
                                            { maximumFractionDigits: 4 }
                                          )
                                    : 'N/A'}
                                </div>
                                <div
                                  className="mt-0.5 truncate text-xs text-gray-500"
                                  style={{ fontFamily: 'Outfit, sans-serif' }}
                                  title={token.mint}
                                >
                                  {token.mint.slice(0, 4)}...
                                  {token.mint.slice(-4)}
                                </div>
                                {/* Token Value Badge for dust swapping */}
                                {isDustSwapEnabled() && (
                                  <TokenValueBadge
                                    estimatedSolValue={getTokenPrice(token.mint)?.estimatedSolValue || 0}
                                    isSwappable={isTokenSwappable(token.mint)}
                                    hasSwapRoute={hasTokenSwapRoute(token.mint)}
                                    meetsMinimum={tokenMeetsMinimum(token.mint)}
                                    isLoading={pricesLoading}
                                    compact
                                  />
                                )}
                              </div>
                            </div>
                            );

                            // Wrap with tooltip if below minimum, otherwise return card directly
                            if (isBelowMinimum) {
                              return (
                                <Tooltip key={token.mint} content={belowMinimumTooltip} position="top">
                                  {tokenCard}
                                </Tooltip>
                              );
                            }
                            // Add key to non-tooltip card
                            return <div key={token.mint}>{tokenCard}</div>;
                          })}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="overflow-y-auto">
                      {!isConnected ? (
                        <div className="py-8 text-center">
                          <p
                            className="text-gray-700"
                            style={{ fontFamily: 'Outfit, sans-serif' }}
                          >
                            Connect your wallet to see your NFTs
                          </p>
                        </div>
                      ) : nftDiscoveryError && nfts.length === 0 ? (
                        <div className="py-8 text-center">
                          <p
                            className="font-semibold"
                            style={{
                              fontFamily: 'Outfit, sans-serif',
                              color: '#FB8B00',
                            }}
                          >
                            NFT discovery temporarily unavailable
                          </p>
                          <p
                            className="mt-2 text-sm text-gray-700"
                            style={{ fontFamily: 'Outfit, sans-serif' }}
                          >
                            {nftDiscoveryError}
                          </p>
                        </div>
                      ) : filteredNfts.length === 0 ? (
                        <div className="py-8 text-center">
                          <p
                            className="text-gray-700"
                            style={{ fontFamily: 'Outfit, sans-serif' }}
                          >
                            {searchQuery
                              ? 'No NFTs found'
                              : 'No NFTs in your wallet'}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* Expand/Collapse All Controls */}
                          {groupedNfts.length > 1 && (
                            <div className="flex items-center justify-between px-1">
                              <span
                                className="text-sm font-medium"
                                style={{ fontFamily: 'Outfit, sans-serif', color: '#50E5F8' }}
                              >
                                {groupedNfts.length} collection{groupedNfts.length !== 1 ? 's' : ''}
                              </span>
                              <div className="flex gap-3">
                                <button
                                  onClick={expandAllCollections}
                                  className="text-xs font-medium transition-all hover:scale-105"
                                  style={{
                                    fontFamily: 'Outfit, sans-serif',
                                    color: '#FB8B00',
                                  }}
                                >
                                  Expand all
                                </button>
                                <button
                                  onClick={collapseAllCollections}
                                  className="text-xs font-medium transition-all hover:scale-105"
                                  style={{
                                    fontFamily: 'Outfit, sans-serif',
                                    color: '#FB8B00',
                                  }}
                                >
                                  Collapse all
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Collection Groups */}
                          {groupedNfts.map(([collectionKey, { name: collectionName, address: collectionAddress, image: collectionImage, nfts: collectionNfts }]) => {
                            const isExpanded = isCollectionExpanded(collectionKey);
                            const selectedCount = collectionNfts.filter(nft => isItemSelected('nft', nft)).length;

                            return (
                              <div
                                key={collectionKey}
                                className="overflow-hidden rounded-xl transition-all"
                                style={{
                                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                  border: isExpanded ? '2px solid #50E5F8' : '2px solid rgba(80, 229, 248, 0.4)',
                                  boxShadow: isExpanded ? '0 4px 20px rgba(80, 229, 248, 0.2)' : 'none',
                                }}
                              >
                                {/* Collection Header */}
                                <button
                                  onClick={() => toggleCollectionExpanded(collectionKey)}
                                  className="flex w-full items-center gap-3 p-3 transition-all hover:brightness-110"
                                  style={{
                                    background: isExpanded
                                      ? 'linear-gradient(135deg, rgba(80, 229, 248, 0.2) 0%, rgba(251, 139, 0, 0.15) 100%)'
                                      : 'transparent',
                                  }}
                                >
                                  {/* Collection Image */}
                                  <div
                                    className="relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg"
                                    style={{
                                      border: '2px solid #50E5F8',
                                      boxShadow: '0 2px 8px rgba(80, 229, 248, 0.3)',
                                    }}
                                  >
                                    <img
                                      src={collectionImage || '/Chest.svg'}
                                      alt={collectionName}
                                      className="h-full w-full object-cover"
                                    />
                                    {/* NFT count overlay */}
                                    <div
                                      className="absolute bottom-0 right-0 rounded-tl px-1.5 py-0.5 text-[10px] font-bold"
                                      style={{
                                        background: 'linear-gradient(92deg, #FB8B00 0%, #F6DF6F 100%)',
                                        color: '#000',
                                      }}
                                    >
                                      {collectionNfts.length}
                                    </div>
                                  </div>

                                  {/* Collection Info */}
                                  <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
                                    <span
                                      className="w-full truncate text-left text-sm font-bold"
                                      style={{
                                        fontFamily: 'Outfit, sans-serif',
                                        color: '#FFFFFF',
                                        textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                                      }}
                                      title={collectionName}
                                    >
                                      {collectionName}
                                    </span>
                                    {collectionAddress && (
                                      <span
                                        className="rounded px-1.5 py-0.5 text-xs font-medium"
                                        style={{
                                          fontFamily: 'monospace',
                                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                          color: '#7EEAF5',
                                        }}
                                        title={collectionAddress}
                                      >
                                        {collectionAddress.slice(0, 6)}...{collectionAddress.slice(-6)}
                                      </span>
                                    )}
                                    {selectedCount > 0 && (
                                      <span
                                        className="mt-0.5 rounded px-2 py-0.5 text-[10px] font-bold"
                                        style={{
                                          fontFamily: 'Outfit, sans-serif',
                                          background: 'linear-gradient(92deg, #FB8B00 0%, #F6DF6F 50%, #FB8B00 100%)',
                                          color: '#000',
                                        }}
                                      >
                                        {selectedCount} MARKED FOR JUNK
                                      </span>
                                    )}
                                  </div>

                                  {/* Expand/Collapse Icon */}
                                  <div
                                    className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-all"
                                    style={{
                                      backgroundColor: isExpanded ? '#50E5F8' : 'rgba(80, 229, 248, 0.3)',
                                      border: '2px solid #50E5F8',
                                    }}
                                  >
                                    {isExpanded ? (
                                      <ChevronDown className="h-5 w-5" style={{ color: '#000' }} />
                                    ) : (
                                      <ChevronRight className="h-5 w-5" style={{ color: '#50E5F8' }} />
                                    )}
                                  </div>
                                </button>

                                {/* Collection NFTs Grid */}
                                {isExpanded && (
                                  <div
                                    className="grid grid-cols-3 gap-3 p-3 pt-0 sm:gap-4 md:grid-cols-4 lg:grid-cols-5"
                                  >
                                    {collectionNfts.map(nft => (
                                      <div
                                        key={nft.mint}
                                        className={`relative flex cursor-pointer flex-col overflow-hidden rounded-lg border-2 transition-all ${
                                          isItemSelected('nft', nft)
                                            ? 'scale-95 border-yellow-400'
                                            : 'hover:scale-[1.02]'
                                        }`}
                                        style={{
                                          borderColor: isItemSelected('nft', nft) ? undefined : '#50E5F8',
                                          boxShadow: isItemSelected('nft', nft)
                                            ? '0 0 12px rgba(251, 191, 36, 0.4)'
                                            : '0 2px 8px rgba(0, 0, 0, 0.3)',
                                        }}
                                        onClick={() => toggleItemSelection('nft', nft)}
                                      >
                                        {/* Marked for Junk Overlay */}
                                        {isItemSelected('nft', nft) && (
                                          <div
                                            className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg"
                                            style={{
                                              background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(251, 139, 0, 0.3) 100%)',
                                            }}
                                          >
                                            <Trash2
                                              className="mb-1 h-8 w-8"
                                              style={{ color: '#FB8B00' }}
                                            />
                                            <div
                                              className="px-2 text-center text-xs font-bold text-white"
                                              style={{ fontFamily: 'Outfit, sans-serif' }}
                                            >
                                              MARKED FOR JUNK
                                            </div>
                                          </div>
                                        )}

                                        <div
                                          className="aspect-square w-full"
                                          style={{ backgroundColor: '#1a1a1a' }}
                                        >
                                          <img
                                            src={nft.image || '/Chest.svg'}
                                            alt={nft.name}
                                            className="h-full w-full object-cover"
                                          />
                                        </div>
                                        <div
                                          className="flex flex-col justify-center p-2"
                                          style={{
                                            backgroundColor: '#1a1a1a',
                                            minHeight: '50px',
                                          }}
                                        >
                                          <div
                                            className="line-clamp-2 text-center text-xs font-bold"
                                            style={{
                                              fontFamily: 'Outfit, sans-serif',
                                              lineHeight: '1.3',
                                              color: '#FFFFFF',
                                            }}
                                            title={nft.name}
                                          >
                                            {nft.name}
                                          </div>
                                          <div
                                            className="mt-1 truncate text-center text-[10px] font-medium"
                                            style={{
                                              fontFamily: 'monospace',
                                              color: '#7EEAF5',
                                            }}
                                            title={nft.mint}
                                          >
                                            {nft.mint.slice(0, 4)}...{nft.mint.slice(-4)}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Right Panel: Selected Items (Desktop only) - 40% width */}
          {!isMobile && (
            <div
              className="w-full md:sticky md:top-4 md:w-2/5 md:self-start"
              style={{ minHeight: '600px', maxHeight: 'calc(100vh - 120px)' }}
            >
              <div
                className="flex h-full flex-col rounded-lg border-2 p-4"
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  borderStyle: 'dashed',
                  borderColor: '#FFFFFF',
                }}
              >
                {/* Summary Stats */}
                {selectedItems.length > 0 ? (
                  <div className="mb-4 grid grid-cols-3 gap-3 text-sm">
                    <div
                      className="rounded p-2 text-center"
                      style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                    >
                      <div
                        className="text-gray-400"
                        style={{ fontFamily: 'Outfit, sans-serif' }}
                      >
                        Assets
                      </div>
                      <div
                        className="font-bold text-yellow-400"
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          fontWeight: 'bold',
                        }}
                      >
                        {selectedItems.length}
                      </div>
                    </div>
                    <div
                      className="rounded p-2 text-center"
                      style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                    >
                      <div
                        className="text-gray-400"
                        style={{ fontFamily: 'Outfit, sans-serif' }}
                      >
                        Credits
                      </div>
                      <div
                        className="flex items-center justify-center gap-1 font-bold text-yellow-400"
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          fontWeight: 'bold',
                        }}
                      >
                        {estimate.net} SOL
                        <div className="text-2xl">💰</div>
                      </div>
                    </div>
                    <div
                      className="rounded p-2 text-center"
                      style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                    >
                      <div
                        className="text-gray-400"
                        style={{ fontFamily: 'Outfit, sans-serif' }}
                      >
                        Points
                      </div>
                      <div
                        className="flex items-center justify-center gap-1 font-bold text-green-400"
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          fontWeight: 'bold',
                        }}
                      >
                        {pointsEstimate}
                        <img
                          src="/points.svg"
                          alt="Points"
                          className="inline h-4 w-4"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mb-4 p-4 text-center">
                    <p
                      className="text-gray-400"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      Please select tokens or NFTs to Junk
                    </p>
                  </div>
                )}

                {/* Dust Swap Section - Shows swappable tokens and PDA balance (staging/dev) */}
                {isDustSwapEnabled() && (
                  <DustSwapSection
                    selectedSwappableTokens={selectedSwappableTokens}
                    prices={prices}
                    isExpanded={dustSwapExpanded}
                    onToggleExpand={() => setDustSwapExpanded(!dustSwapExpanded)}
                    onRouteClick={(mint) => setShowSwapRoutePopup(mint)}
                    isLoading={pricesLoading}
                    onRefreshPrices={refreshPrices}
                    walletAddress={address}
                  />
                )}

                {/* Selected Items Grid with Pagination */}
                <div
                  className="flex flex-1 flex-col overflow-hidden"
                  style={{ minHeight: 0 }}
                >
                  <div
                    className="mb-3 flex-shrink-0 text-sm font-medium text-white"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    Selected Items:
                  </div>
                  {selectedItems.length === 0 ? (
                    <div className="flex flex-1 items-center justify-center p-4 text-center">
                      <p
                        className="text-sm text-gray-400"
                        style={{ fontFamily: 'Outfit, sans-serif' }}
                      >
                        Please select tokens or NFTs to Junk
                      </p>
                    </div>
                  ) : (
                    <>
                      <div
                        className="mb-3 flex-1 overflow-x-hidden overflow-y-auto pr-1"
                        style={{ minHeight: 0, maxHeight: '280px' }}
                        onWheel={e => {
                          const element = e.currentTarget;
                          const atTop = element.scrollTop === 0;
                          const atBottom =
                            Math.abs(
                              element.scrollHeight -
                                element.scrollTop -
                                element.clientHeight
                            ) < 1;

                          if (
                            (atTop && e.deltaY < 0) ||
                            (atBottom && e.deltaY > 0)
                          ) {
                            e.preventDefault();
                          }
                        }}
                      >
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                          {selectedItems.map((selected, index) => {
                            // Check if this token is swappable
                            const isSwappable = selected.type === 'token' &&
                              selectedSwappableTokens.some(t => t.mint === selected.item.mint);

                            return (
                              <div
                                key={`${selected.type}-${selected.item.mint}-${index}`}
                                className="relative overflow-hidden rounded-lg border"
                                style={{
                                  backgroundColor: '#FFFFFF',
                                  borderColor: isSwappable ? '#4ADE80' : '#50E5F8',
                                }}
                              >
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    toggleItemSelection(
                                      selected.type,
                                      selected.item
                                    );
                                  }}
                                  className="absolute top-0.5 right-0.5 z-10 flex h-5 w-5 items-center justify-center rounded-full text-xs text-white transition-colors hover:bg-red-500"
                                  style={{
                                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                  }}
                                >
                                  ×
                                </button>

                                <div className="p-2">
                                  <div
                                    className="mb-2 flex aspect-square items-center justify-center rounded"
                                    style={{ backgroundColor: '#F3F4F6' }}
                                  >
                                    {selected.type === 'token' &&
                                    'logoUrl' in selected.item &&
                                    selected.item.logoUrl ? (
                                      <img
                                        src={selected.item.logoUrl}
                                        alt={selected.item.symbol}
                                        className="h-10 w-10 rounded-full"
                                        onError={e => {
                                          (e.target as HTMLImageElement).src =
                                            '/token-placeholder.svg';
                                        }}
                                      />
                                    ) : selected.type === 'nft' &&
                                      'image' in selected.item &&
                                      selected.item.image ? (
                                      <img
                                        src={selected.item.image}
                                        alt={selected.item.name}
                                        className="h-full w-full rounded object-cover"
                                      />
                                    ) : (
                                      <img
                                        src="/token-placeholder.svg"
                                        alt={selected.item.symbol}
                                        className="h-10 w-10 object-contain"
                                      />
                                    )}
                                  </div>

                                  {/* Swappable indicator with Credits value */}
                                  {isSwappable && (
                                    <div
                                      className="mb-1 text-center text-[10px] font-semibold leading-tight"
                                      style={{
                                        fontFamily: 'Outfit, sans-serif',
                                        color: '#4ADE80',
                                      }}
                                    >
                                      {(() => {
                                        const priceData = getTokenPrice(selected.item.mint);
                                        const value = priceData?.estimatedSolValue || 0;
                                        if (value === 0) return '0 Credits';
                                        if (value < 0.0001) return '<0.0001 Credits';
                                        if (value < 0.001) return `${value.toFixed(4)} Credits`;
                                        if (value < 0.01) return `${value.toFixed(3)} Credits`;
                                        return `${value.toFixed(2)} Credits`;
                                      })()}
                                    </div>
                                  )}

                                  <div className="text-center">
                                    <div
                                      className="line-clamp-1 text-xs font-bold"
                                      style={{
                                        fontFamily: 'Outfit, sans-serif',
                                        color: '#000000',
                                      }}
                                      title={selected.item.symbol}
                                    >
                                      {selected.item.symbol}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Junk Button (Desktop) */}
                {selectedItems.length > 0 && (
                  <div className="mt-auto flex-shrink-0 pt-3">
                    <button
                      onClick={() => setShowConfirmModal(true)}
                      disabled={isBurning || isSwapping || isCombinedJunking}
                      className="w-full transform px-6 py-3 text-lg font-bold text-black transition-all hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        borderRadius: '100px',
                        background:
                          'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                        boxShadow: '4px 4px 12px 0 rgba(0, 0, 0, 0.50)',
                      }}
                    >
                      {isBurning ? 'BURNING...' : isSwapping ? 'SWAPPING...' : isCombinedJunking ? 'JUNKING...' : 'TRASH JUNK'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Mobile: Floating Action Button for Trash Junk */}
        {isMobile && selectedItems.length > 0 && (
          <button
            onClick={() => setShowConfirmModal(true)}
            disabled={isBurning || isSwapping || isCombinedJunking}
            className="fixed right-4 bottom-[100px] left-4 z-40 transform px-6 py-3 text-lg font-bold text-black transition-all hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              fontFamily: 'Outfit, sans-serif',
              borderRadius: '100px',
              background:
                'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
            }}
          >
            {isBurning ? 'BURNING...' : isSwapping ? 'SWAPPING...' : isCombinedJunking ? 'JUNKING...' : 'TRASH JUNK'}
          </button>
        )}
      </main>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div
          className="bg-opacity-70 fixed inset-0 flex items-center justify-center bg-black"
          style={{ zIndex: 9999 }}
        >
          <div
            className="mx-4 w-full max-w-md rounded-lg border-l-4 p-6"
            style={{
              backgroundColor: 'rgba(255, 193, 7, 0.95)',
              borderColor: '#FEBB01',
            }}
          >
            <div className="flex items-start space-x-3 mb-6">
              <span className="text-4xl">⛔</span>
              <div>
                <h3
                  className="mb-2 text-xl font-bold"
                  style={{ fontFamily: 'Outfit, sans-serif', color: '#000' }}
                >
                  WARNING
                </h3>
                <p className="font-medium mb-3" style={{ fontFamily: 'Outfit, sans-serif', color: '#000' }}>
                  Any tokens marked for burn will be permanently destroyed. Please make absolutely sure you are not junking tokens you intend to hold by mistake. This action cannot be reversed, and Junk Fun cannot recover your tokens.
                </p>
                <p className="text-sm" style={{ fontFamily: 'Outfit, sans-serif', color: '#000' }}>
                  By clicking CONFIRM JUNK, you agree that Junk Fun is not held liable for any tokens you burn by mistake.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 transform px-6 py-3 font-bold transition-all hover:scale-105"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  borderRadius: '100px',
                  backgroundColor: '#6B7280',
                  color: '#FFF',
                  boxShadow: '4px 4px 12px 0 rgba(0, 0, 0, 0.50)',
                }}
              >
                CANCEL
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  handleDump();
                }}
                className="flex-1 transform px-6 py-3 font-bold transition-all hover:scale-105"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  borderRadius: '100px',
                  background:
                    'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                  color: '#000',
                  boxShadow: '4px 4px 12px 0 rgba(0, 0, 0, 0.50)',
                }}
              >
                CONFIRM JUNK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals and Drawers */}
      <RewardModal
        open={rewardModalData.show}
        onClose={() => setRewardModalData({ show: false, rewards: [] })}
        rewards={rewardModalData.rewards}
      />

      <HistoryDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} />
      <FaqDrawer open={faqOpen} onClose={() => setFaqOpen(false)} />
      <PrizeTiersDrawer
        open={prizeTiersOpen}
        onClose={() => setPrizeTiersOpen(false)}
      />
      <SettingsSheet
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />

      {/* Invitation Modals */}
      <InvitationRequiredModal
        open={showInviteRequired}
        onClose={() => setShowInviteRequired(false)}
        onOpenInviteModal={() => setShowInviteModal(true)}
        actionName="burn assets"
      />
      <InvitationCodeModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
      />

      {/* Combined Dump Modal (Swap + Burn Progress) */}
      <CombinedDumpModal
        isOpen={showCombinedModal}
        operationType={combinedOperationType}
        currentStep={combinedStep}
        swapTokenCount={swapTokenCount}
        swapCurrentIndex={swapCurrentIndex}
        swapCurrentSymbol={swapCurrentSymbol}
        estimatedSwapSol={estimatedSwapSol}
        burnTokenCount={burnTokenCount}
        burnNftCount={burnNftCount}
        estimatedBurnSol={estimatedBurnSol}
        attemptCount={combinedAttemptCount}
        isEmbeddedWallet={isEmbeddedWallet}
        error={combinedError}
      />

      {/* Burn Success Modal */}
      {showBurnSuccess && (lastBurnResult.signatures.length > 0 || lastBurnResult.tokensSwapped > 0) && (
        <div className="bg-opacity-70 fixed inset-0 z-50 flex flex-col items-center justify-center bg-black">
          <div className="relative flex h-full w-full flex-col">
            {/* Top Gray Bar with Title - Full Width */}
            <div
              className="px-8 py-6 text-center"
              style={{
                backgroundColor: 'rgba(40, 40, 40, 0.95)',
                borderTop: '3px solid #50E4F9',
                borderBottom: '3px solid #50E4F9',
              }}
            >
              <h1
                className="text-5xl font-bold md:text-6xl"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  color: '#FFFFFF',
                }}
              >
                JUNK RECLAIMED
              </h1>
            </div>

            {/* Middle Content Area with Background */}
            <div className="relative flex flex-1 items-center justify-center overflow-hidden max-md:items-start max-md:overflow-y-auto max-md:py-2">
              {/* Background Image */}
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: 'url(/raccoonbanner.svg)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  opacity: 0.3,
                  filter: 'brightness(0.5)',
                }}
              />

              {/* Content Container */}
              <div className="relative z-10 w-full max-w-6xl px-8">
                {/* Rewards Section */}
                <div className="mx-auto grid max-w-3xl grid-cols-1 gap-6 md:grid-cols-2">
                  {/* Credits Received */}
                  <div className="text-center">
                    <h2
                      className="mb-4 text-2xl md:text-3xl"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 700,
                        color: '#FFFFFF',
                      }}
                    >
                      Credits Received
                    </h2>
                    <div
                      className="flex min-h-[180px] flex-col items-center justify-center rounded-xl p-6"
                      style={{
                        backgroundColor: 'rgba(80, 228, 249, 0.1)',
                        border: '2px solid #50E4F9',
                      }}
                    >
                      <div className="mb-3 text-4xl">💰</div>
                      <div
                        className="text-3xl font-bold"
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          fontWeight: 700,
                          color: '#FEBB01',
                        }}
                      >
                        {lastBurnResult.netSol}
                      </div>
                      <div
                        className="mt-2 text-sm"
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          color: 'rgba(255, 255, 255, 0.7)',
                        }}
                      >
                        Available to use
                      </div>
                    </div>
                  </div>

                  {/* Points Earned */}
                  <div className="text-center">
                    <h2
                      className="mb-4 text-2xl md:text-3xl"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 700,
                        color: '#FFFFFF',
                      }}
                    >
                      Points Earned
                    </h2>
                    <div
                      className="flex min-h-[180px] flex-col items-center justify-center rounded-xl p-6"
                      style={{
                        backgroundColor: 'rgba(251, 139, 0, 0.1)',
                        border: '2px solid #FB8B00',
                      }}
                    >
                      <img
                        src="/points.svg"
                        alt="Points"
                        className="mb-3 h-16 w-16"
                      />
                      <div
                        className="text-3xl font-bold"
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          fontWeight: 700,
                          color: '#FFFFFF',
                        }}
                      >
                        {lastBurnResult.pointsEarned}
                      </div>
                      <div
                        className="mt-2 text-sm"
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          color: 'rgba(255, 255, 255, 0.7)',
                        }}
                      >
                        Total earned
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Gray Bar with Action Buttons - Full Width */}
            <div
              className="px-8 py-6"
              style={{
                backgroundColor: 'rgba(40, 40, 40, 0.95)',
                borderTop: '3px solid #50E4F9',
              }}
            >
              {/* Action Buttons */}
              <div className="mb-4 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <button
                  onClick={() => {
                    setShowBurnSuccess(false);
                    navigate('/chest');
                  }}
                  className="w-full transform px-12 py-4 text-xl font-bold transition-all hover:scale-105 sm:w-auto"
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    borderRadius: '100px',
                    background: 'linear-gradient(135deg, #50E4F9, #00CED1)',
                    color: '#000000',
                    boxShadow: '0 0 20px rgba(80, 228, 249, 0.6)',
                  }}
                >
                  OPEN CHESTS
                </button>

                <button
                  onClick={async () => {
                    const pointsEarned = lastBurnResult.pointsEarned || 0;
                    try {
                      const result = await shareJunk(pointsEarned);
                      if (result.pointsEarned > 0) {
                        toast.success(`+${result.pointsEarned} Points!`, {
                          description: 'Share tracked successfully',
                        });
                      } else {
                        toast.info('Share opened', {
                          description: result.message,
                        });
                      }
                    } catch (error) {
                      toast.error('Failed to track share', {
                        description:
                          error instanceof Error
                            ? error.message
                            : 'Unknown error',
                      });
                    }
                  }}
                  disabled={isSharing}
                  className="flex w-full transform items-center justify-center gap-2 px-12 py-4 text-xl font-bold transition-all hover:scale-105 disabled:opacity-50 sm:w-auto"
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    borderRadius: '100px',
                    backgroundColor: 'transparent',
                    border: '2px solid #FFFFFF',
                    color: '#FFFFFF',
                  }}
                >
                  <Share2 size={20} />
                  {getPotentialPoints('junk') > 0 ? (
                    <>
                      SHARE TO GET {getPotentialPoints('junk')}{' '}
                      <img src="/points.svg" alt="Points" className="h-6 w-6" />
                    </>
                  ) : (
                    'SHARE ON X'
                  )}
                </button>
              </div>

              {/* Quit Button */}
              <div className="text-center">
                <button
                  onClick={() => setShowBurnSuccess(false)}
                  className="text-white underline transition-opacity hover:opacity-70"
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: '18px',
                  }}
                >
                  QUIT
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Swap Route Popup */}
      {isDustSwapEnabled() && (
        <SwapRoutePopup
          open={showSwapRoutePopup !== null}
          onClose={() => setShowSwapRoutePopup(null)}
          token={showSwapRoutePopup ? displayedTokens.find(t => t.mint === showSwapRoutePopup) || null : null}
          priceData={showSwapRoutePopup ? getTokenPrice(showSwapRoutePopup) || null : null}
        />
      )}

      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
}
